<template>
  <BaseDrawer>
    <template #trigger>
      <BaseTooltip
        :description="descriptionOverride || description"
        v-bind="$attrs"
        class="*:hover:text-primary! *:cursor-pointer!"
        @click="triggerEvent('wiki-tooltip-click')"
      />
    </template>
    <template #content>
      <TextDrawerTitle class="border-b border-gray-100 p-4 md:p-6">
        <BaseIcon name="information" filled class="text-tooltip-500 mb-1" />
        {{ title }}
      </TextDrawerTitle>
      <div class="p-4 text-base font-normal md:p-6">
        <!-- Risks due to v-html are mitigated with DOMPurify -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="wiki" v-html="content" />
      </div>
    </template>
  </BaseDrawer>
</template>

<script setup>
import { ref, onMounted } from "vue"
import { triggerEvent } from "@/services/plausibleService.js"
import useWikiStore from "@/stores/wikiStore.js"
import useNotificationStore from "@/stores/notificationStore.js"
import {
  BaseIcon,
  TextDrawerTitle,
  BaseDrawer,
  BaseTooltip,
} from "@repowerednl/ui-component-library"

const props = defineProps({
  /**
   * Relative path to the wiki page (no extension) to load. For example:
   * "/nl/platform/insights/about"
   */
  path: {
    type: String,
    required: true,
  },
  /**
   * Overrides the description pulled from the wiki page.
   */
  descriptionOverride: {
    type: [String, Boolean],
    default: false,
  },
})

defineOptions({
  inheritAttrs: false,
})

const LOADING_MESSAGE = "Documentatie laden..."

const wikiStore = useWikiStore()
const notificationStore = useNotificationStore()
const title = ref(LOADING_MESSAGE)
const description = ref(LOADING_MESSAGE)
const content = ref(LOADING_MESSAGE)

onMounted(() => {
  wikiStore.loadPage(
    props.path,
    (page) => {
      title.value = page.title
      description.value = page.description
      content.value = page.content
    },
    (error) => {
      notificationStore.pushError(
        "Documentatie niet beschikbaar",
        `Sommige beschrijvingen konden niet worden opgehaald (code: ${error.code})`,
        "loading-wiki-failed",
      )
    },
  )
})
</script>

<!-- Global style to make it work with rendered HTML -->
<style>
.wiki h1 {
  margin-bottom: 0.5rem;
  margin-top: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
  padding-bottom: 0.5rem;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 400;
}

.wiki h2 {
  margin-bottom: 0.25rem;
  margin-top: 1rem;
  font-size: 1.25rem;
  line-height: 1.75rem;
  font-weight: 500;
}

.wiki h3 {
  margin-bottom: 0.25rem;
  margin-top: 0.5rem;
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 700;
}

.wiki p {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.wiki ul {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  list-style-type: disc;
  padding-left: 1rem;
}

.wiki ul::marker {
  color: var(--color-primary-500);
}

.wiki ol {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  list-style-type: decimal;
  padding-left: 1rem;
}

.wiki ol::marker {
  color: var(--color-primary-500);
}

.wiki img {
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  border-radius: 1rem;
  border: 1px solid var(--color-gray-200);
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
}

.wiki a {
  text-decoration: underline;
  text-decoration-color: var(--color-primary-500);
  text-decoration-thickness: 2px;
}

.wiki a:hover {
  color: var(--color-primary-500);
}
</style>
